/**
 * Real fullscreen — hiding the browser chrome and the taskbar, not merely
 * filling the viewport.
 *
 * Two things make this fiddly:
 *
 *  1. requestFullscreen() needs a USER GESTURE, and the gesture context is
 *     lost across an `await`. So it must be called synchronously inside the
 *     click handler, before tuning. Hence `enterFullscreen` being callable on
 *     its own rather than buried in an effect.
 *
 *  2. Old WebKit/Chromium (WebOS, Fire TV) only has the webkit-prefixed
 *     forms, and some TV WebViews have neither — the app is already
 *     fullscreen there, so failing silently is the correct behaviour.
 */

type FsElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
  webkitEnterFullscreen?: () => void;
  msRequestFullscreen?: () => void;
};

type FsDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitExitFullscreen?: () => Promise<void> | void;
  msExitFullscreen?: () => void;
};

export function isFullscreenSupported(): boolean {
  const d = document as FsDocument;
  return !!(document.fullscreenEnabled || (d as any).webkitFullscreenEnabled);
}

/**
 * NATIVE VIDEO FULLSCREEN — the fallback where there is no real fallback.
 *
 * iOS has never let a custom element call requestFullscreen(); what it DOES
 * support, on the <video> element specifically, is the older iOS-only
 * `webkitEnterFullscreen()` — Apple's own full-screen video player takes
 * over the whole screen, with its own play/pause/scrub/AirPlay controls.
 * This is a different thing from `enterFullscreen()` above, not a shim for
 * it: the custom control bar (double-tap-to-seek, our Stop button, and so
 * on) is not present while Apple's player is on screen, because it IS
 * Apple's player, not ours.
 *
 * Checked on the actual <video> element, not the document, because the
 * method exists only on HTMLVideoElement — an iPad in desktop-Safari mode
 * can report neither this nor `isFullscreenSupported()`, which correctly
 * hides the button rather than wiring it to something that won't fire.
 */
type FsVideoElement = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
  webkitDisplayingFullscreen?: boolean;
};

export function isNativeVideoFullscreenSupported(video: HTMLVideoElement | null): boolean {
  return typeof (video as FsVideoElement | null)?.webkitEnterFullscreen === 'function';
}

export function enterNativeVideoFullscreen(video: HTMLVideoElement | null): boolean {
  const v = video as FsVideoElement | null;
  if (!v?.webkitEnterFullscreen) return false;
  try {
    v.webkitEnterFullscreen();
    return true;
  } catch {
    // No gesture, or the browser changed its mind mid-call. Nothing to
    // degrade to here — the CSS theater view is still on screen underneath.
    return false;
  }
}

export function currentFullscreenElement(): Element | null {
  const d = document as FsDocument;
  return document.fullscreenElement ?? d.webkitFullscreenElement ?? null;
}

export async function enterFullscreen(el: HTMLElement | null): Promise<boolean> {
  if (!el) return false;
  const e = el as FsElement;
  try {
    if (e.requestFullscreen) { await e.requestFullscreen({ navigationUI: 'hide' }); return true; }
    if (e.webkitRequestFullscreen) { await e.webkitRequestFullscreen(); return true; }
    if (e.msRequestFullscreen) { e.msRequestFullscreen(); return true; }
  } catch {
    // Denied (no gesture) or unsupported. The CSS fallback still fills the
    // viewport, so this degrades rather than breaks.
  }
  return false;
}

export async function exitFullscreen(): Promise<void> {
  const d = document as FsDocument;
  if (!currentFullscreenElement()) return;
  try {
    if (document.exitFullscreen) await document.exitFullscreen();
    else if (d.webkitExitFullscreen) await d.webkitExitFullscreen();
    else if (d.msExitFullscreen) d.msExitFullscreen();
  } catch { /* already exited */ }
}

/** Fires when fullscreen changes for ANY reason, including F11 and browser Esc. */
export function onFullscreenChange(cb: (active: boolean) => void): () => void {
  const handler = () => cb(!!currentFullscreenElement());
  document.addEventListener('fullscreenchange', handler);
  document.addEventListener('webkitfullscreenchange', handler);
  return () => {
    document.removeEventListener('fullscreenchange', handler);
    document.removeEventListener('webkitfullscreenchange', handler);
  };
}
